import moment from "moment";
import { IHotel28355 } from "../../../common/types";
import { IHotelRoomWithDetails } from "../../../firestore/firestoreService/RoomPricePerDayStore";

// Builds a travel date list and user-facing date summary for the list header.
export const buildDateSummary = (
  startDate?: string | null,
  endDate?: string | null,
) => {
  const nights =
    startDate && endDate ? moment(endDate).diff(moment(startDate), "days") : 0;
  const dateSummary =
    startDate && endDate
      ? `${moment(startDate).format("D MMM YYYY")} - ${moment(endDate).format(
          "D MMM YYYY",
        )}`
      : "";
  const travelDates =
    startDate && endDate && nights > 0
      ? Array.from({ length: nights }, (_, index) =>
          moment(startDate, "YYYY-MM-DD")
            .add(index, "days")
            .format("YYYY-MM-DD"),
        )
      : [];
  return { nights, dateSummary, travelDates };
};

// Formats the guests summary that appears in list items.
export const buildGuestsSummary = (
  roomsDefault: number,
  adultDefault: number,
  childrenDefault: number,
) =>
  `${roomsDefault} δωμάτιο${roomsDefault > 1 ? "α" : ""}, ${adultDefault} ενήλικες${childrenDefault ? `, ${childrenDefault} παιδιά` : ""}`;

// Normalizes room summaries to ensure required flags are always present.
export const buildRoomSummaries = (rooms: IHotelRoomWithDetails[]) =>
  rooms.map((room) => ({
    ...room,
    minStay: room.minStay ?? 1,
    hasPositiveAllotment: Boolean(room.hasPositiveAllotment),
    allZeroAllotment: Boolean(room.allZeroAllotment),
    allPricesStopSale: Boolean(room.allPricesStopSale),
    anyStopSale: Boolean(room.anyStopSale),
    hasOnRequestPeriod: Boolean(room.hasOnRequestPeriod),
    availableRoomsCount: room.availableRoomsCount ?? 0,
  }));

// Computes hotel-level pricing and availability using the attached room summaries.
export const buildProcessedHotels = (
  hotelsWithRooms: IHotel28355[],
  currentHotels: IHotel28355[],
) => {
  const dataset = hotelsWithRooms.length ? hotelsWithRooms : currentHotels;
  return dataset.map((hotel: any) => {
    const roomSummaries = buildRoomSummaries(
      (hotel.rooms || []) as IHotelRoomWithDetails[],
    );
    const roomsSorted = [...roomSummaries].sort(
      (a, b) => (a?.totalPrice || 0) - (b?.totalPrice || 0),
    );
    const cheapestRoom = roomsSorted[0];
    const hotelStopSale =
      roomsSorted.length > 0 && roomsSorted.every((r) => r.allPricesStopSale);
    const cheapestAvailableRoom = roomsSorted.find(
      (r) =>
        !r.onRequest &&
        r.hasPositiveAllotment &&
        !r.anyStopSale &&
        (r.availableRoomsCount || 0) > 0,
    );
    const cheapestOnRequestRoom = roomsSorted.find(
      (r) =>
        !r.allPricesStopSale &&
        (r.onRequest || r.allZeroAllotment || r.hasOnRequestPeriod),
    );
    const primaryRoom =
      cheapestAvailableRoom || cheapestOnRequestRoom || cheapestRoom;
    const hasDirectAvailability =
      !!primaryRoom &&
      !primaryRoom.onRequest &&
      primaryRoom.hasPositiveAllotment &&
      !primaryRoom.anyStopSale &&
      (primaryRoom.availableRoomsCount || 0) > 0;
    const hasOnRequestRooms =
      !!primaryRoom &&
      (primaryRoom.allZeroAllotment || primaryRoom.hasOnRequestPeriod) &&
      !primaryRoom.allPricesStopSale;
    const minStay = primaryRoom?.minStay || cheapestRoom?.minStay || 1;
    const displayPrice =
      primaryRoom?.totalPrice ??
      cheapestRoom?.totalPrice ??
      Number.POSITIVE_INFINITY;
    const onRequestType =
      primaryRoom?.onRequest || (hasOnRequestRooms && hasDirectAvailability)
        ? "full"
        : hasOnRequestRooms
          ? "short"
          : null;
    return {
      id: hotel.id,
      hotel,
      rooms: roomsSorted,
      minStay,
      displayPrice,
      hasDirectAvailability,
      onRequestType,
      hotelStopSale,
      primaryRoom,
    };
  });
};

// Filters hotels that are either bookable or explicitly on-request.
export const filterAvailableHotels = (
  processedHotels: any[],
  hotelsWithRoomsCount: number,
) =>
  processedHotels.filter((h) => {
    if (h.hotelStopSale || !h.rooms?.length) return false;
    return Number.isFinite(h.displayPrice) || hotelsWithRoomsCount > 0;
  });

// Splits the list by minimum-stay eligibility.
// Applies the chosen sorting mode to a list of hotels.
export const applySorting = (list: any[], sorting: string) => {
  const arr = [...list];
  switch (sorting) {
    case "alpha":
      return arr.sort((a, b) =>
        a.hotel?.name?.localeCompare(b.hotel?.name || "", "el", {
          sensitivity: "base",
        }),
      );
    case "price-desc":
      return arr.sort((a, b) => (b.displayPrice || 0) - (a.displayPrice || 0));
    case "price-asc":
    default:
      return arr.sort((a, b) => (a.displayPrice || 0) - (b.displayPrice || 0));
  }
};

// Builds the final display list in the correct order.
export const orderHotelsForDisplay = (
  hotels: any[],
  requestedNights: number,
  sorting: string,
) => {
  const available: any[] = [];
  const onRequest: any[] = [];
  const suggestions: any[] = [];

  for (const hotel of hotels) {
    const isSuggestionOnly =
      requestedNights > 0 && Number(hotel.minStay || 0) > requestedNights;
    if (isSuggestionOnly) {
      suggestions.push({ ...hotel, isMinStay: true });
      continue;
    }
    if (!hotel.hasDirectAvailability && hotel.onRequestType) {
      onRequest.push(hotel);
      continue;
    }
    available.push(hotel);
  }

  return [
    ...applySorting(available, sorting),
    ...applySorting(suggestions, sorting),
    ...applySorting(onRequest, sorting),
  ];
};

// Moves a selected hotel to the top of the list while keeping the rest visible.
export const promoteSelectedHotel = (
  hotels: any[],
  selectedHotelId?: string,
) => {
  if (!selectedHotelId) return hotels;
  const index = hotels.findIndex(
    (hotel) =>
      hotel?.id === selectedHotelId || hotel?.hotel?.id === selectedHotelId,
  );
  if (index <= 0) return hotels;
  return [hotels[index], ...hotels.slice(0, index), ...hotels.slice(index + 1)];
};

// Filters hotels based on URL params (city and specific hotel selection).
export const filterHotelsByParams = (hotels: any[], params: any) => {
  const cityParam = params?.city;
  const cityFilters =
    cityParam && cityParam !== "all" ? cityParam.split("-") : [];

  return hotels.filter((record) => {
    if (!cityFilters.length) return true;
    const cityValue =
      record?.hotel?.city?.code ||
      record?.hotel?.city?.id ||
      record?.hotel?.city?.value ||
      record?.hotel?.city;
    if (!cityValue) return false;
    return cityFilters.includes(`${cityValue}`);
  });
};
